import { Button } from "@/components/ui/Button/Button";
import { Box, Card, Collapse, Flex, Group, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import type { ReactElement } from "react";
import { useEffect, useMemo } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { ToggleSwitch } from "@onengine/component-library";
import { errorTreeHasAnyPath } from "@/utils/formErrors";
import { FORM_VALIDATION_FAILED_EVENT } from "@/utils/scrollToFirstError";
import styles from "./CollapsableCard.module.scss";

type CollapsableCardProps = {
  title: string;
  titleAction?: string;
  onTitleActionClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  hasActions?: boolean;
  titleIcon?: ReactElement;
  rightAction?: string;
  hasRightActionIcon?: boolean;
  rightActionIcon?: ReactElement;
  rightActionDisabled?: boolean;
  rightActionLoading?: boolean;
  cancelAction?: string;
  cancelActionDisabled?: boolean;
  onCancelActionClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  secondaryAction?: string;
  secondaryActionDisabled?: boolean;
  secondaryActionLoading?: boolean;
  onSecondaryActionClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onRightActionClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  headerActions?: React.ReactNode;
  badge?: React.ReactNode;
  children?: React.ReactNode;
  description?: string;
  customToggleButton?: React.ReactNode;
  defaultState?: boolean;
  isToggleSwitch?: boolean;
  toggleChecked?: boolean;
  onToggleChange?: (checked: boolean) => void;
  mb?: string;
  border?: boolean;
  fieldKeys?: readonly string[];
};

export default function CollapsableCard({
  title,
  hasActions,
  titleAction,
  titleIcon,
  rightAction,
  onRightActionClick,
  hasRightActionIcon = true,
  rightActionIcon,
  rightActionDisabled = false,
  rightActionLoading = false,
  cancelAction,
  cancelActionDisabled = false,
  onCancelActionClick,
  secondaryAction,
  secondaryActionDisabled = false,
  secondaryActionLoading = false,
  onSecondaryActionClick,
  children,
  onTitleActionClick,
  headerActions,
  badge,
  isToggleSwitch = false,
  toggleChecked = false,
  onToggleChange,
  border = false,
  description,
  customToggleButton,
  defaultState = true,
  mb = "var(--spacing-medium)",
  fieldKeys,
}: CollapsableCardProps) {
  const [opened, { toggle, open, close }] = useDisclosure(
    isToggleSwitch ? toggleChecked : defaultState,
  );

  const hasFormContext = !!useFormContext();
  const shouldSubscribeToErrors = hasFormContext && !!fieldKeys?.length;

  useEffect(() => {
    if (isToggleSwitch) {
      if (toggleChecked) {
        open();
      } else {
        close();
      }
    }
  }, [toggleChecked, isToggleSwitch, open, close]);

  useEffect(() => {
    if (!fieldKeys?.length || isToggleSwitch) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ fieldName?: string; errors?: any }>).detail;
      if (!detail) return;

      if (detail.errors && errorTreeHasAnyPath(detail.errors, fieldKeys)) {
        open();
        return;
      }

      const targetField = detail.fieldName;
      if (!targetField) return;
      const matches = fieldKeys.some((key) => {
        if (key === targetField) return true;
        if (targetField.startsWith(key + ".")) return true;
        if (key.startsWith(targetField + ".")) return true;
        return false;
      });
      if (matches) open();
    };
    window.addEventListener(FORM_VALIDATION_FAILED_EVENT, handler);
    return () => window.removeEventListener(FORM_VALIDATION_FAILED_EVENT, handler);
  }, [fieldKeys, isToggleSwitch, open]);

  const handleToggleChange = (checked: boolean) => {
    if (checked) {
      open();
    } else {
      close();
    }
    onToggleChange?.(checked);
  };

  return (
    <Card className={styles.card} mb={mb} withBorder={border}>
      {shouldSubscribeToErrors && <ErrorAwareOpener fieldKeys={fieldKeys!} onError={open} />}
      <Group className={styles.cardTitle} onClick={isToggleSwitch ? undefined : toggle}>
        <Stack gap={"var(--spacing-3x-small, 4px)"}>
          <Title className={styles.title}>
            {/*
              The accordion control is a real button around the title text alone, never the
              whole header: the header also holds its own Buttons and ToggleSwitch, and a
              `role="button"` wrapper would make all of them presentational to a screen reader
              (WCAG 4.1.2, axe `nested-interactive`). It carries no onClick of its own — the
              native click it raises bubbles to the Group above and toggles once, so Enter and
              Space work with no key handler.
            */}
            {isToggleSwitch ? (
              title
            ) : (
              <button type="button" className={styles.titleToggle} aria-expanded={opened}>
                {title}
              </button>
            )}
            {(titleAction || titleIcon) && (
              <Button
                variant="tertiary"
                size="small"
                iconRight={titleIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onTitleActionClick?.(e);
                }}
              >
                {titleAction}
              </Button>
            )}
            {badge && <Box style={{ marginLeft: "var(--spacing-small)" }}> {badge} </Box>}
          </Title>
          {description && <Text className={styles.descriptionText}>{description}</Text>}
        </Stack>

        <Flex className={styles.rightActions} gap="var(--spacing-small)" align="center">
          {hasActions && (
            <Flex className={styles.buttons}>
              {cancelAction && (
                <Button
                  variant="tertiary"
                  size="small"
                  disabled={cancelActionDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                    onCancelActionClick?.(e);
                  }}
                >
                  {cancelAction}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  variant="tertiary"
                  size="small"
                  disabled={secondaryActionDisabled}
                  loading={secondaryActionLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                    onSecondaryActionClick?.(e);
                  }}
                >
                  {secondaryAction}
                </Button>
              )}
              {rightAction && (
                <Button
                  variant="neutral-secondary"
                  iconLeft={rightActionIcon || (hasRightActionIcon ? <IconPlus /> : undefined)}
                  size="small"
                  disabled={rightActionDisabled}
                  loading={rightActionLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                    onRightActionClick?.(e);
                  }}
                >
                  {rightAction}
                </Button>
              )}
            </Flex>
          )}
          {headerActions && !isToggleSwitch && (
            <Flex className={styles.buttons} gap="var(--spacing-small)">
              {headerActions}
            </Flex>
          )}

          {customToggleButton ? (
            <>
              {opened ? (
                <Text
                  style={{
                    color: "var(--text-default, #363D4D)",
                    fontSize: "var(--text-font-size-medium, 14px)",
                    fontWeight: 500,
                  }}
                >
                  Collapse
                </Text>
              ) : (
                customToggleButton
              )}
            </>
          ) : isToggleSwitch ? (
            <Group align="center">
              <Text size="var(--text-font-size-large)" fw="var(--font-weight-regular)">
                {toggleChecked ? "Enabled" : "Disabled"}
              </Text>{" "}
              <ToggleSwitch checked={toggleChecked} onChange={handleToggleChange} size="xs" />
            </Group>
          ) : (
            <IconChevronDown className={`${styles.chevron} ${opened ? styles.chevronOpen : ""}`} />
          )}
        </Flex>
      </Group>
      <Collapse className={styles.collapse} in={opened}>
        {children}
      </Collapse>
    </Card>
  );
}

function ErrorAwareOpener({
  fieldKeys,
  onError,
}: {
  fieldKeys: readonly string[];
  onError: () => void;
}) {
  const stableKey = useMemo(() => fieldKeys.join("|"), [fieldKeys]);
  const stableFieldKeys = useMemo(() => [...fieldKeys], [stableKey]);
  const { errors } = useFormState();
  const hasError = errorTreeHasAnyPath(errors, stableFieldKeys);

  useEffect(() => {
    if (hasError) {
      onError();
    }
  }, [hasError, onError]);

  return null;
}
